/**
 * SchemaMarkup — Injects JSON-LD structured data as inline <script> tags.
 *
 * Rendered synchronously by React (no Helmet) so react-snap captures the
 * schemas in the prerendered HTML. JSON-LD is valid anywhere in the document.
 *
 * Props:
 *   - data: { "@type": "Service" | "FAQPage" | "Blog" | "BlogPosting" | "Product" | "ImageGallery", ...fields }
 *   - faqData: optional [{ question, answer }, ...] — emits a separate FAQPage schema
 */
const SchemaMarkup = ({ data, faqData }) => {
  const siteUrl = "https://epoxyart33.com";
  const schemas = [];

  // ---------- Service ----------
  if (data && data["@type"] === "Service") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": data["@id"] || `${siteUrl}/#service-${(data.name || 'svc').toLowerCase().replace(/\s+/g, '-')}`,
      "name": data.name,
      "description": data.description,
      "provider": {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#localbusiness`,
        "name": "Epoxy Art 33",
        "alternateName": "EpoxyArt33",
        "telephone": "+1-239-276-1462",
        "url": siteUrl
      },
      "areaServed": data.areaServed || "Naples, FL",
      "serviceType": data.serviceType || "Epoxy Flooring"
    });
  }

  // ---------- WebPage ----------
  if (data && data["@type"] === "WebPage") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": data["@id"] || `${siteUrl}${data.url || ''}#webpage`,
      "name": data.name,
      "description": data.description,
      "url": data.url || siteUrl,
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "name": "EpoxyArt33",
        "url": siteUrl
      },
      "publisher": {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#localbusiness`,
        "name": "Epoxy Art 33",
        "alternateName": "EpoxyArt33"
      }
    });
  }

  // ---------- FAQPage from faqData prop ----------
  if (faqData && Array.isArray(faqData) && faqData.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
  }

  // ---------- Other types passed via data prop ----------
  if (data && data["@type"] && data["@type"] !== "Service" && data["@type"] !== "WebPage") {
    if (data["@type"] === "FAQPage") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": data["@id"] || `${siteUrl}/faq#faqpage`,
        "name": data.name,
        "description": data.description,
        "mainEntity": data.mainEntity || []
      });
    } else if (data["@type"] === "ImageGallery") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "ImageGallery",
        "@id": data["@id"] || `${siteUrl}/gallery#gallery`,
        "name": data.name,
        "description": data.description
      });
    } else if (data["@type"] === "Blog") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Blog",
        "@id": data["@id"] || `${siteUrl}/blog#blog`,
        "name": data.name,
        "description": data.description,
        "publisher": {
          "@type": "Organization",
          "name": "Epoxy Art 33",
          "alternateName": "EpoxyArt33",
          "url": siteUrl
        }
      });
    } else if (data["@type"] === "BlogPosting") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "@id": data["@id"] || `${siteUrl}/blog#post`,
        "headline": data.headline,
        "description": data.description,
        "image": data.image,
        "author": {
          "@type": "Organization",
          "name": data.author || "Epoxy Art 33"
        },
        "datePublished": data.datePublished,
        "dateModified": data.dateModified || data.datePublished,
        "publisher": {
          "@type": "Organization",
          "name": "Epoxy Art 33",
          "alternateName": "EpoxyArt33",
          "logo": {
            "@type": "ImageObject",
            "url": `${siteUrl}/logos/logo-wordmark.png`
          }
        }
      });
    } else if (data["@type"] === "Product") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": data["@id"] || `${siteUrl}/products#products`,
        "name": data.name,
        "description": data.description,
        "brand": data.brand
      });
    }
  }

  if (schemas.length === 0) return null;

  return (
    <>
      {schemas.map((schema, i) => (
        <script key={i} data-seo="" type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </>
  );
};

export default SchemaMarkup;
